/**
 * G2eGente - Gestão de Atividades do Funcionário
 * Comparação de requisitos, atribuição de atividades e estágio probatório
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeeActivitiesAPI, probationPeriodsAPI, employeesAPI, jobPositionsAPI } from '../../lib/api';
import ChangeRequestsTab from '../../components/company/ChangeRequestsTab';
import CargosVerificacao from '../../components/company/CargosVerificacao';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus, 
  Search, 
  Loader2,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  Briefcase,
  Bell,
  FileText as FileTextIcon,
  GraduationCap,
  FileText,
  RefreshCw,
  Calendar,
  Target,
  ClipboardCheck,
  History,
  FileDown,
  Eye,
  Edit,
  MoreHorizontal,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';

// Tipos de atribuição
const TIPOS_ATRIBUICAO = [
  { value: 'agregada', label: 'Função Agregada', desc: 'Adiciona função sem substituir a atual' },
  { value: 'substituicao', label: 'Substituição Temporária', desc: 'Substitui temporariamente outro funcionário' },
  { value: 'promocao', label: 'Promoção', desc: 'Promoção para novo cargo' },
  { value: 'probatorio', label: 'Estágio Probatório', desc: 'Período de avaliação antes da efetivação' }
];

// Mapa de tipos para labels amigáveis
const TIPO_LABELS = {
  'agregada': 'Função Agregada',
  'substituicao': 'Substituição',
  'promocao': 'Promoção',
  'probatorio': 'Estágio Probatório'
};

// Status de atribuição
const STATUS_LABELS = {
  'pendente_inicio': { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
  'em_andamento': { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700' },
  'avaliacao': { label: 'Em Avaliação', color: 'bg-purple-100 text-purple-700' },
  'efetivado': { label: 'Efetivado', color: 'bg-green-100 text-green-700' },
  'retornado': { label: 'Retornado', color: 'bg-orange-100 text-orange-700' },
  'cancelado': { label: 'Cancelado', color: 'bg-slate-100 text-slate-700' },
  'ativo': { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  'concluido': { label: 'Concluído', color: 'bg-slate-100 text-slate-700' }
};

const ATIVIDADE_EXCLUIVEL = ['em_andamento', 'pendente_inicio', 'prorrogado'];
const PROBATORIO_CONCLUIDO = ['concluido', 'cancelado', 'encerrado', 'finalizado', 'efetivado'];


export default function GestaoAtividades() {
  const { selectedCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [probatorios, setProbatorios] = useState([]);
  const [alertas, setAlertas] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('solicitacoes');
  
  // Comparison state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showProrrogacaoModal, setShowProrrogacaoModal] = useState(false);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedProbatorio, setSelectedProbatorio] = useState(null);
  const [historicoData, setHistoricoData] = useState(null);
  
  // Form states
  const [assignForm, setAssignForm] = useState({
    employee_id: '',
    cargo_id: '',
    motivo: '',
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: '',
    temporario: true,
    tipo: 'agregada',
    adicional_funcao: '',
    requer_probatorio: false,
    duracao_probatorio_dias: 90
  });
  
  const [decisionForm, setDecisionForm] = useState({
    decisao: '',
    justificativa: '',
    enviar_para_pdi: false
  });
  
  const [prorrogacaoForm, setProrrogacaoForm] = useState({
    nova_data: '',
    motivo: ''
  });

  useEffect(() => {
    fetchData();
    fetchAlertas();
  }, [selectedCompany?.company_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesRes, positionsRes, activitiesRes, probatoriosRes] = await Promise.all([
        employeesAPI.getAll({ situacao: 'ativo' }),
        jobPositionsAPI.getAll(),
        employeeActivitiesAPI.list(),
        probationPeriodsAPI.list()
      ]);
      setEmployees(employeesRes.data);
      setPositions(positionsRes.data);
      setActivities(activitiesRes.data);
      setProbatorios(probatoriosRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertas = async () => {
    try {
      const res = await probationPeriodsAPI.getAlertas();
      setAlertas(res.data);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    }
  };

  const handleEnviarNotificacoes = async () => {
    try {
      const res = await probationPeriodsAPI.enviarNotificacoes();
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Erro ao enviar notificações');
    }
  };

  const handleViewHistorico = async (employeeId) => {
    try {
      const res = await employeeActivitiesAPI.getHistoricoCargos(employeeId);
      setHistoricoData(res.data);
      setShowHistoricoModal(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const generateHistoricoPDF = async (employeeId) => {
    try {
      const res = await employeeActivitiesAPI.getHistoricoCargosForPdf(employeeId);
      const data = res.data;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;
      
      // Título
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('HISTÓRICO DE MOVIMENTAÇÕES DE CARGO', pageWidth / 2, y, { align: 'center' });
      y += 15;
      
      // Empresa
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Empresa: ${data.empresa?.nome || '-'}`, 20, y);
      y += 5;
      doc.text(`CNPJ: ${data.empresa?.cnpj || '-'}`, 20, y);
      y += 10;
      
      // Funcionário
      doc.setFont('helvetica', 'bold');
      doc.text('DADOS DO FUNCIONÁRIO', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      doc.text(`Nome: ${data.funcionario?.nome || '-'}`, 20, y);
      y += 5;
      doc.text(`CPF: ${data.funcionario?.cpf || '-'}`, 20, y);
      y += 5;
      doc.text(`Matrícula: ${data.funcionario?.matricula || '-'}`, 20, y);
      y += 5;
      doc.text(`Setor: ${data.funcionario?.setor || '-'}`, 20, y);
      y += 5;
      doc.text(`Data de Admissão: ${data.funcionario?.data_admissao || '-'}`, 20, y);
      y += 5;
      doc.text(`Cargo Atual: ${data.funcionario?.cargo_atual || '-'}`, 20, y);
      y += 12;
      
      // Resumo
      doc.setFont('helvetica', 'bold');
      doc.text('RESUMO', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      doc.text(`Tempo na empresa: ${data.resumo?.tempo_empresa || '-'}`, 20, y);
      y += 5;
      doc.text(`Total de movimentações: ${data.resumo?.total_movimentacoes || 0}`, 20, y);
      y += 5;
      doc.text(`Cargo inicial: ${data.resumo?.cargo_inicial || '-'}`, 20, y);
      y += 12;
      
      // Timeline
      doc.setFont('helvetica', 'bold');
      doc.text('HISTÓRICO DE MOVIMENTAÇÕES', 20, y);
      y += 8;
      
      if (data.timeline?.length > 0) {
        for (const evento of data.timeline) {
          if (y > 270) {
            doc.addPage();
            y = 20;
          }
          
          doc.setFont('helvetica', 'bold');
          doc.text(`${evento.data_formatada} - ${evento.tipo}`, 20, y);
          doc.setFont('helvetica', 'normal');
          y += 5;
          
          if (evento.cargo_de && evento.cargo_para) {
            doc.text(`  ${evento.cargo_de} → ${evento.cargo_para}`, 20, y);
            y += 5;
          }
          
          if (evento.descricao) {
            const lines = doc.splitTextToSize(`  ${evento.descricao}`, pageWidth - 40);
            doc.text(lines, 20, y);
            y += lines.length * 5;
          }
          
          y += 5;
        }
      } else {
        doc.text('Nenhuma movimentação registrada', 20, y);
      }
      
      // Rodapé
      doc.setFontSize(8);
      doc.text(`Gerado em: ${data.data_geracao}`, 20, 285);
      
      doc.save(`historico_cargos_${data.funcionario?.nome?.replace(/\s/g, '_') || 'funcionario'}.pdf`);
      toast.success('PDF gerado com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleCompare = async () => {
    if (!selectedEmployee || !selectedCargo) {
      toast.error('Selecione o funcionário e o cargo');
      return;
    }

    try {
      setLoadingComparison(true);
      const res = await employeeActivitiesAPI.compareRequirements(selectedEmployee, selectedCargo);
      setComparison(res.data);
    } catch (error) {
      toast.error('Erro ao comparar requisitos');
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleAssign = async () => {
    if (!assignForm.employee_id || !assignForm.cargo_id || !assignForm.motivo || !assignForm.data_inicio) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const payload = {
        ...assignForm,
        adicional_funcao: assignForm.adicional_funcao ? parseFloat(assignForm.adicional_funcao) : null,
        duracao_probatorio_dias: assignForm.requer_probatorio ? parseInt(assignForm.duracao_probatorio_dias) : null
      };
      
      await employeeActivitiesAPI.create(payload);
      toast.success('Atividade atribuída com sucesso');
      setShowAssignModal(false);
      resetAssignForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir atividade');
    }
  };

  const handleEncerrar = async () => {
    if (!selectedActivity || !decisionForm.decisao || !decisionForm.justificativa) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await employeeActivitiesAPI.encerrar(selectedActivity.id, decisionForm);
      toast.success(`Atribuição encerrada: ${decisionForm.decisao}`);
      setShowDecisionModal(false);
      setDecisionForm({ decisao: '', justificativa: '', enviar_para_pdi: false });
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao encerrar atribuição');
    }
  };

  const handleProrrogar = async () => {
    if (!selectedActivity || !prorrogacaoForm.nova_data) {
      toast.error('Informe a nova data');
      return;
    }

    try {
      await employeeActivitiesAPI.prorrogar(selectedActivity.id, prorrogacaoForm);
      toast.success('Vigência prorrogada com sucesso');
      setShowProrrogacaoModal(false);
      setProrrogacaoForm({ nova_data: '', motivo: '' });
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao prorrogar');
    }
  };

  const resetAssignForm = () => {
    setAssignForm({
      employee_id: '',
      cargo_id: '',
      motivo: '',
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: '',
      temporario: true,
      tipo: 'agregada',
      adicional_funcao: '',
      requer_probatorio: false,
      duracao_probatorio_dias: 90
    });
  };

  const openAssignFromComparison = () => {
    if (comparison) {
      setAssignForm({
        ...assignForm,
        employee_id: comparison.employee_id,
        cargo_id: comparison.cargo_novo_id
      });
      setShowAssignModal(true);
    }
  };

  const openActivityDetail = async (activity) => {
    try {
      const res = await employeeActivitiesAPI.getById(activity.id);
      setSelectedActivity(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const generateAditivoPDF = async (probatorio) => {
    try {
      const res = await probationPeriodsAPI.getAditivoPdf(probatorio.id);
      const data = res.data;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;
      
      // Título
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('ADITIVO AO CONTRATO DE TRABALHO', pageWidth / 2, y, { align: 'center' });
      y += 8;
      doc.text('ESTÁGIO PROBATÓRIO EM NOVA FUNÇÃO', pageWidth / 2, y, { align: 'center' });
      y += 15;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      // Empresa
      doc.setFont('helvetica', 'bold');
      doc.text('EMPRESA:', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      doc.text(`Razão Social: ${data.empresa.razao_social || '-'}`, 20, y);
      y += 5;
      doc.text(`CNPJ: ${data.empresa.cnpj || '-'}`, 20, y);
      y += 5;
      doc.text(`Endereço: ${data.empresa.endereco || '-'}`, 20, y);
      y += 12;
      
      // Empregado
      doc.setFont('helvetica', 'bold');
      doc.text('EMPREGADO:', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      doc.text(`Nome: ${data.empregado.nome || '-'}`, 20, y);
      y += 5;
      doc.text(`CPF: ${data.empregado.cpf || '-'}`, 20, y);
      y += 5;
      doc.text(`Matrícula: ${data.empregado.matricula || '-'}`, 20, y);
      y += 15;
      
      // Cláusulas
      doc.setFont('helvetica', 'bold');
      doc.text('Cláusula 1 - DO OBJETO', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      const texto1 = `O presente aditivo tem por objeto a designação temporária do empregado para exercer a função de ${data.empregado.cargo_novo || '[CARGO]'}, pelo período de ${data.probatorio.duracao_dias} dias, a título de estágio probatório.`;
      const lines1 = doc.splitTextToSize(texto1, pageWidth - 40);
      doc.text(lines1, 20, y);
      y += lines1.length * 5 + 8;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Cláusula 2 - DA FUNÇÃO ANTERIOR', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      const texto2 = `O empregado ocupava anteriormente a função de ${data.empregado.cargo_anterior || '[CARGO ANTERIOR]'}, para a qual retornará automaticamente caso não seja efetivado na nova função.`;
      const lines2 = doc.splitTextToSize(texto2, pageWidth - 40);
      doc.text(lines2, 20, y);
      y += lines2.length * 5 + 8;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Cláusula 3 - DO PERÍODO', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      doc.text(`Início: ${data.probatorio.data_inicio}`, 20, y);
      y += 5;
      doc.text(`Término Previsto: ${data.probatorio.data_fim_prevista}`, 20, y);
      y += 12;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Cláusula 4 - DA REMUNERAÇÃO', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      if (data.probatorio.adicional_funcao) {
        doc.text(`O empregado receberá adicional de função no valor de R$ ${data.probatorio.adicional_funcao.toFixed(2)}`, 20, y);
      } else {
        doc.text('A remuneração será mantida conforme contrato original.', 20, y);
      }
      y += 12;
      
      doc.setFont('helvetica', 'bold');
      doc.text('Cláusula 5 - DA AVALIAÇÃO', 20, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
      doc.text('O empregado será avaliado periodicamente conforme critérios estabelecidos pela empresa.', 20, y);
      y += 30;
      
      // Assinaturas
      doc.line(20, y, 90, y);
      doc.line(120, y, 190, y);
      y += 5;
      doc.text('Empresa', 55, y, { align: 'center' });
      doc.text('Empregado', 155, y, { align: 'center' });
      y += 10;
      doc.text(`Data: ___/___/____`, pageWidth / 2, y, { align: 'center' });
      
      doc.save(`aditivo_probatorio_${data.empregado.nome?.replace(/\s/g, '_') || 'funcionario'}.pdf`);
      toast.success('PDF gerado com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleDeleteActivity = async (activity) => {
    if (!window.confirm(`Excluir definitivamente a atribuição de ${activity.employee_nome}?\n\nO estágio probatório ligado, se houver, será mantido. Esta ação não pode ser desfeita.`)) return;
    try {
      await employeeActivitiesAPI.delete(activity.id);
      toast.success('Atribuição excluída');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir atribuição');
    }
  };

  const handleDeleteProbatorio = async (probatorio) => {
    if (!window.confirm(`Excluir definitivamente o estágio probatório de ${probatorio.employee_nome}?\n\nA atribuição ligada, se houver, será mantida. Esta ação não pode ser desfeita.`)) {
      return;
    }
    
    try {
      await probationPeriodsAPI.delete(probatorio.id);
      toast.success('Estágio probatório excluído com sucesso');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao excluir estágio probatório';
      toast.error(msg);
    }
  };

  const filteredActivities = activities.filter(a => {
    if (!search) return true;
    return a.employee_nome?.toLowerCase().includes(search.toLowerCase()) ||
           a.cargo_novo_nome?.toLowerCase().includes(search.toLowerCase());
  });

  const filteredProbatorios = probatorios.filter(p => {
    if (!search) return true;
    return p.employee_nome?.toLowerCase().includes(search.toLowerCase()) ||
           p.cargo_novo_titulo?.toLowerCase().includes(search.toLowerCase());
  });

  // Stats
  const stats = {
    atribuicoes_ativas: activities.filter(a => ['em_andamento', 'avaliacao', 'ativo'].includes(a.status)).length,
    probatorios_ativos: probatorios.filter(p => p.status === 'ativo').length,
    vencendo_30_dias: activities.filter(a => {
      if (!a.data_fim_prevista && !a.data_fim) return false;
      const dataFim = new Date(a.data_fim_prevista || a.data_fim);
      const hoje = new Date();
      const diff = (dataFim - hoje) / (1000 * 60 * 60 * 24);
      return diff > 0 && diff <= 30 && ['em_andamento', 'ativo'].includes(a.status);
    }).length,
    aguardando_decisao: activities.filter(a => a.status === 'avaliacao').length
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="employee-activities-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestão de Atividades</h1>
            <p className="text-slate-600">Atribuição de atividades, estágio probatório e gestão de carreiras</p>
          </div>
          <Button 
            onClick={() => { resetAssignForm(); setShowAssignModal(true); }}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="btn-nova-atribuicao"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Atribuição
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.atribuicoes_ativas}</p>
                  <p className="text-xs text-slate-500">Atribuições Ativas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.probatorios_ativos}</p>
                  <p className="text-xs text-slate-500">Estágios Probatórios</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.vencendo_30_dias}</p>
                  <p className="text-xs text-slate-500">Vencendo em 30 dias</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <ClipboardCheck className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.aguardando_decisao}</p>
                  <p className="text-xs text-slate-500">Aguardando Decisão</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alertas */}
        {alertas && alertas.total_alertas > 0 && (
          <Alert className="border-amber-200 bg-amber-50">
            <Bell className="w-4 h-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Alertas de Atenção</AlertTitle>
            <AlertDescription className="text-amber-700">
              <div className="flex flex-wrap gap-4 mt-2">
                {alertas.alertas.vencendo_7_dias?.length > 0 && (
                  <Badge className="bg-red-100 text-red-700">
                    {alertas.alertas.vencendo_7_dias.length} probatório(s) vencendo em 7 dias
                  </Badge>
                )}
                {alertas.alertas.avaliacao_pendente?.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">
                    {alertas.alertas.avaliacao_pendente.length} avaliação(ões) pendente(s)
                  </Badge>
                )}
                {alertas.alertas.aditivo_pendente?.length > 0 && (
                  <Badge className="bg-purple-100 text-purple-700">
                    {alertas.alertas.aditivo_pendente.length} aditivo(s) não assinado(s)
                  </Badge>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={handleEnviarNotificacoes}
              >
                <Bell className="w-4 h-4 mr-2" />
                Enviar Notificações
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Comparison Tool */}
        <Card className="border-purple-200">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100">
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <RefreshCw className="w-5 h-5" />
              Comparação de Requisitos
            </CardTitle>
            <CardDescription>
              Compare o que o funcionário atende vs o que é necessário para uma nova atividade
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Funcionário</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger data-testid="select-employee-compare">
                    <SelectValue placeholder="Selecione o funcionário" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nome} - {emp.cargo || 'Sem cargo'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Nova Atividade/Cargo</Label>
                <Select value={selectedCargo} onValueChange={setSelectedCargo}>
                  <SelectTrigger data-testid="select-cargo-compare">
                    <SelectValue placeholder="Selecione o cargo" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.id} value={pos.id}>
                        {pos.titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button 
                  onClick={handleCompare}
                  disabled={loadingComparison || !selectedEmployee || !selectedCargo}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="btn-comparar"
                >
                  {loadingComparison ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Comparar
                </Button>
              </div>
            </div>

            {/* Comparison Results */}
            {comparison && (
              <div className="mt-6 space-y-4">
                <div className={`p-4 rounded-lg border-2 ${
                  comparison.apto_para_atividade 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        comparison.apto_para_atividade ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {comparison.apto_para_atividade ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-6 h-6 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {comparison.employee_nome}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {comparison.cargo_atual || 'Sem cargo'} → {comparison.cargo_novo_nome}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-slate-900">
                        {comparison.percentual_atendimento}%
                      </p>
                      <p className="text-sm text-slate-600">Atendimento</p>
                    </div>
                  </div>
                  <Progress value={comparison.percentual_atendimento} className="mt-3" />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Treinamentos */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-cyan-600" />
                        Treinamentos
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {comparison.treinamentos_atendidos?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-green-600 uppercase mb-1">Atendidos</p>
                          {comparison.treinamentos_atendidos.map((t, idx) => (
                            <div key={idx} className="flex items-center gap-2 py-1">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm">{t.nome}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {comparison.treinamentos_vencidos?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-amber-600 uppercase mb-1">Vencidos</p>
                          {comparison.treinamentos_vencidos.map((t, idx) => (
                            <div key={idx} className="flex items-center gap-2 py-1">
                              <Clock className="w-4 h-4 text-amber-500" />
                              <span className="text-sm">{t.nome}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {comparison.treinamentos_faltantes?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-600 uppercase mb-1">Pendentes</p>
                          {comparison.treinamentos_faltantes.map((t, idx) => (
                            <div key={idx} className="flex items-center gap-2 py-1">
                              <XCircle className="w-4 h-4 text-red-500" />
                              <span className="text-sm">{t.nome}</span>
                              {t.obrigatorio && (
                                <Badge variant="outline" className="text-red-600">Obrigatório</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {!comparison.treinamentos_atendidos?.length && 
                       !comparison.treinamentos_faltantes?.length && 
                       !comparison.treinamentos_vencidos?.length && (
                        <p className="text-sm text-slate-500">Nenhum treinamento específico requerido</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Conhecimentos */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                        Conhecimentos e Escolaridade
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-slate-600 uppercase mb-2">Escolaridade</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm">Funcionário: {comparison.escolaridade_funcionario || 'Não informada'}</p>
                            <p className="text-sm">Requerida: {comparison.escolaridade_requerida || 'Não especificada'}</p>
                          </div>
                          {comparison.escolaridade_ok ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </div>

                      {comparison.conhecimentos_atendidos?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-green-600 uppercase mb-1">Conhecimentos Atendidos</p>
                          <div className="flex flex-wrap gap-1">
                            {comparison.conhecimentos_atendidos.map((c, idx) => (
                              <Badge key={idx} className="bg-green-100 text-green-700">{c}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {comparison.conhecimentos_faltantes?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-600 uppercase mb-1">Conhecimentos Necessários</p>
                          <div className="flex flex-wrap gap-1">
                            {comparison.conhecimentos_faltantes.map((c, idx) => (
                              <Badge key={idx} variant="outline" className="text-red-600">{c}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={openAssignFromComparison}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="btn-atribuir-comparacao"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Atribuir Esta Atividade
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="solicitacoes">Solicitações</TabsTrigger>
            <TabsTrigger value="atribuicoes">Atribuições</TabsTrigger>
            <TabsTrigger value="probatorios">Estágios Probatórios</TabsTrigger>
            <TabsTrigger value="verificacao-cargos">Verificação de Cargos</TabsTrigger>
            <TabsTrigger value="historico">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="solicitacoes" className="mt-4">
            <ChangeRequestsTab />
          </TabsContent>

          <TabsContent value="verificacao-cargos" className="mt-4">
            <CargosVerificacao />
          </TabsContent>

          <TabsContent value="atribuicoes" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Atribuições de Atividades</CardTitle>
                <CardDescription>Funcionários com atividades atribuídas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Funcionário</TableHead>
                      <TableHead>Cargo Anterior</TableHead>
                      <TableHead></TableHead>
                      <TableHead>Nova Atividade</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Vigência</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredActivities.filter(a => !['cancelado', 'efetivado', 'retornado'].includes(a.status)).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          Nenhuma atribuição ativa
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredActivities.filter(a => !['cancelado', 'efetivado', 'retornado'].includes(a.status)).map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell className="font-medium">{activity.employee_nome}</TableCell>
                          <TableCell>{activity.cargo_anterior_titulo || activity.employee_cargo_atual || '-'}</TableCell>
                          <TableCell>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-purple-100 text-purple-700">
                              {activity.cargo_novo_nome || activity.cargo_novo_titulo}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{TIPO_LABELS[activity.tipo] || 'Função Agregada'}</span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {new Date(activity.data_inicio).toLocaleDateString('pt-BR')}
                            {(activity.data_fim_prevista || activity.data_fim) && 
                              ` - ${new Date(activity.data_fim_prevista || activity.data_fim).toLocaleDateString('pt-BR')}`
                            }
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_LABELS[activity.status]?.color || 'bg-slate-100'}>
                              {STATUS_LABELS[activity.status]?.label || activity.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openActivityDetail(activity)}
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleViewHistorico(activity.employee_id)}
                                title="Histórico de cargos"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedActivity(activity);
                                  setProrrogacaoForm({ nova_data: '', motivo: '' });
                                  setShowProrrogacaoModal(true);
                                }}
                                title="Prorrogar"
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedActivity(activity);
                                  setDecisionForm({ decisao: '', justificativa: '', enviar_para_pdi: false });
                                  setShowDecisionModal(true);
                                }}
                                title="Decisão final"
                              >
                                <ClipboardCheck className="w-4 h-4" />
                              </Button>
                              {ATIVIDADE_EXCLUIVEL.includes(activity.status) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteActivity(activity)}
                                  title="Excluir atribuição"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-atribuicao-${activity.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="probatorios" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Estágios Probatórios</CardTitle>
                <CardDescription>Funcionários em período de avaliação</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Funcionário</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Duração</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Objetivos</TableHead>
                      <TableHead>Avaliações</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProbatorios.filter(p => p.status === 'ativo').length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          Nenhum estágio probatório ativo
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProbatorios.filter(p => p.status === 'ativo').map((prob) => (
                        <TableRow key={prob.id}>
                          <TableCell className="font-medium">{prob.employee_nome}</TableCell>
                          <TableCell>{prob.cargo_novo_titulo}</TableCell>
                          <TableCell>{prob.duracao_dias} dias</TableCell>
                          <TableCell className="text-sm">
                            {new Date(prob.data_inicio).toLocaleDateString('pt-BR')} - {new Date(prob.data_fim_prevista).toLocaleDateString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {prob.objetivos_concluidos || 0}/{prob.total_objetivos || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {prob.total_avaliacoes || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={prob.aditivo_assinado ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {prob.aditivo_assinado ? 'Assinado' : prob.aditivo_gerado ? 'Aguardando Assinatura' : 'Pendente'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {prob.activity_assignment_id && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => generateAditivoPDF(prob)}
                                title="Gerar Aditivo PDF"
                              >
                                <FileDown className="w-4 h-4" />
                              </Button>
                              )}
                              {!PROBATORIO_CONCLUIDO.includes(prob.status) && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteProbatorio(prob)}
                                title="Excluir Estágio Probatório"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-probatorio-${prob.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="historico" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Atribuições</CardTitle>
                <CardDescription>Atribuições encerradas (efetivadas, retornadas ou canceladas)</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Funcionário</TableHead>
                      <TableHead>Cargo Anterior → Novo</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Resultado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredActivities.filter(a => ['cancelado', 'efetivado', 'retornado'].includes(a.status)).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          Nenhum histórico encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredActivities.filter(a => ['cancelado', 'efetivado', 'retornado'].includes(a.status)).map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell className="font-medium">{activity.employee_nome}</TableCell>
                          <TableCell>
                            {activity.cargo_anterior_titulo || '-'} → {activity.cargo_novo_nome || activity.cargo_novo_titulo}
                          </TableCell>
                          <TableCell>{TIPO_LABELS[activity.tipo] || 'Função Agregada'}</TableCell>
                          <TableCell className="text-sm">
                            {new Date(activity.data_inicio).toLocaleDateString('pt-BR')}
                            {activity.data_fim_efetiva && ` - ${new Date(activity.data_fim_efetiva).toLocaleDateString('pt-BR')}`}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_LABELS[activity.status]?.color || 'bg-slate-100'}>
                              {STATUS_LABELS[activity.status]?.label || activity.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modal: Nova Atribuição */}
        <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nova Atribuição de Atividade</DialogTitle>
              <DialogDescription>
                Atribua uma atividade ou cargo ao funcionário
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Funcionário *</Label>
                <Select 
                  value={assignForm.employee_id} 
                  onValueChange={(v) => setAssignForm({ ...assignForm, employee_id: v })}
                >
                  <SelectTrigger data-testid="select-employee-assign">
                    <SelectValue placeholder="Selecione o funcionário" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nome} - {emp.cargo || 'Sem cargo'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Nova Atividade/Cargo *</Label>
                <Select 
                  value={assignForm.cargo_id} 
                  onValueChange={(v) => setAssignForm({ ...assignForm, cargo_id: v })}
                >
                  <SelectTrigger data-testid="select-cargo-assign">
                    <SelectValue placeholder="Selecione o cargo/atividade" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.id} value={pos.id}>
                        {pos.titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tipo de Atribuição *</Label>
                <Select 
                  value={assignForm.tipo} 
                  onValueChange={(v) => setAssignForm({ 
                    ...assignForm, 
                    tipo: v,
                    requer_probatorio: v === 'probatorio'
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_ATRIBUICAO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div>
                          <span className="font-medium">{tipo.label}</span>
                          <span className="text-xs text-slate-500 ml-2">{tipo.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="motivo">Motivo da Atribuição *</Label>
                <Textarea
                  id="motivo"
                  value={assignForm.motivo}
                  onChange={(e) => setAssignForm({ ...assignForm, motivo: e.target.value })}
                  placeholder="Descreva o motivo..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data_inicio">Data Início *</Label>
                  <Input
                    id="data_inicio"
                    type="date"
                    value={assignForm.data_inicio}
                    onChange={(e) => setAssignForm({ ...assignForm, data_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="data_fim">Data Fim Prevista</Label>
                  <Input
                    id="data_fim"
                    type="date"
                    value={assignForm.data_fim}
                    onChange={(e) => setAssignForm({ ...assignForm, data_fim: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="adicional_funcao">Adicional de Função (R$)</Label>
                <Input
                  id="adicional_funcao"
                  type="number"
                  step="0.01"
                  value={assignForm.adicional_funcao}
                  onChange={(e) => setAssignForm({ ...assignForm, adicional_funcao: e.target.value })}
                  placeholder="0.00"
                />
                <p className="text-xs text-slate-500 mt-1">Valor adicional quando o cargo novo tem salário maior</p>
              </div>

              {(assignForm.tipo === 'probatorio' || assignForm.requer_probatorio) && (
                <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-900">Estágio Probatório</span>
                  </div>
                  <div>
                    <Label>Duração do Período</Label>
                    <Select 
                      value={String(assignForm.duracao_probatorio_dias)} 
                      onValueChange={(v) => setAssignForm({ ...assignForm, duracao_probatorio_dias: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="45">45 dias</SelectItem>
                        <SelectItem value="90">90 dias</SelectItem>
                        <SelectItem value="180">180 dias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAssign} className="bg-cyan-600 hover:bg-cyan-700">
                Atribuir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Detalhes da Atribuição */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Detalhes da Atribuição</DialogTitle>
            </DialogHeader>

            {selectedActivity && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Funcionário</Label>
                    <p className="font-medium">{selectedActivity.employee_nome}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Status</Label>
                    <Badge className={STATUS_LABELS[selectedActivity.status]?.color}>
                      {STATUS_LABELS[selectedActivity.status]?.label}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Cargo Anterior</Label>
                    <p>{selectedActivity.cargo_anterior_titulo || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Novo Cargo</Label>
                    <p className="font-medium text-purple-700">{selectedActivity.cargo_novo_nome || selectedActivity.cargo_novo_titulo}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500">Tipo</Label>
                  <p className="capitalize">{selectedActivity.tipo || 'Agregada'}</p>
                </div>

                <div>
                  <Label className="text-slate-500">Motivo</Label>
                  <p>{selectedActivity.motivo}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Data Início</Label>
                    <p>{new Date(selectedActivity.data_inicio).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Data Fim Prevista</Label>
                    <p>{selectedActivity.data_fim_prevista ? new Date(selectedActivity.data_fim_prevista).toLocaleDateString('pt-BR') : '-'}</p>
                  </div>
                </div>

                {selectedActivity.adicional_funcao && (
                  <div>
                    <Label className="text-slate-500">Adicional de Função</Label>
                    <p>R$ {selectedActivity.adicional_funcao.toFixed(2)}</p>
                  </div>
                )}

                {selectedActivity.prorrogacoes?.length > 0 && (
                  <div>
                    <Label className="text-slate-500">Histórico de Prorrogações</Label>
                    <div className="mt-2 space-y-2">
                      {selectedActivity.prorrogacoes.map((p, i) => (
                        <div key={i} className="p-2 bg-slate-50 rounded text-sm">
                          <p>{new Date(p.data_original).toLocaleDateString('pt-BR')} → {new Date(p.nova_data).toLocaleDateString('pt-BR')}</p>
                          {p.motivo && <p className="text-slate-500">{p.motivo}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedActivity.observacoes && (
                  <div>
                    <Label className="text-slate-500">Observações</Label>
                    <p className="whitespace-pre-wrap text-sm">{selectedActivity.observacoes}</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Decisão Final */}
        <Dialog open={showDecisionModal} onOpenChange={setShowDecisionModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Encerrar Atribuição</DialogTitle>
              <DialogDescription>
                Defina a decisão final para esta atribuição
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div>
                <Label>Decisão *</Label>
                <Select 
                  value={decisionForm.decisao} 
                  onValueChange={(v) => setDecisionForm({ ...decisionForm, decisao: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a decisão" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efetivacao">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Efetivar no novo cargo</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="retorno">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-orange-600 rotate-180" />
                        <span>Retornar ao cargo anterior</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="cancelado">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-slate-600" />
                        <span>Cancelar atribuição</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="justificativa">Justificativa *</Label>
                <Textarea
                  id="justificativa"
                  value={decisionForm.justificativa}
                  onChange={(e) => setDecisionForm({ ...decisionForm, justificativa: e.target.value })}
                  placeholder="Descreva a justificativa para esta decisão..."
                  rows={3}
                />
              </div>

              {decisionForm.decisao === 'retorno' && (
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div>
                    <Label>Enviar objetivos para PDI</Label>
                    <p className="text-xs text-slate-500">Objetivos não atingidos serão convertidos em ações de PDI</p>
                  </div>
                  <Switch
                    checked={decisionForm.enviar_para_pdi}
                    onCheckedChange={(v) => setDecisionForm({ ...decisionForm, enviar_para_pdi: v })}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDecisionModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleEncerrar} 
                className={decisionForm.decisao === 'efetivacao' ? 'bg-green-600 hover:bg-green-700' : 'bg-cyan-600 hover:bg-cyan-700'}
              >
                Confirmar Decisão
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Prorrogar Vigência */}
        <Dialog open={showProrrogacaoModal} onOpenChange={setShowProrrogacaoModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Prorrogar Vigência</DialogTitle>
              <DialogDescription>
                Estenda o período da atribuição
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="nova_data">Nova Data de Término *</Label>
                <Input
                  id="nova_data"
                  type="date"
                  value={prorrogacaoForm.nova_data}
                  onChange={(e) => setProrrogacaoForm({ ...prorrogacaoForm, nova_data: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="motivo_prorrogacao">Motivo</Label>
                <Textarea
                  id="motivo_prorrogacao"
                  value={prorrogacaoForm.motivo}
                  onChange={(e) => setProrrogacaoForm({ ...prorrogacaoForm, motivo: e.target.value })}
                  placeholder="Motivo da prorrogação..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProrrogacaoModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleProrrogar} className="bg-cyan-600 hover:bg-cyan-700">
                Prorrogar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Histórico de Cargos */}
        <Dialog open={showHistoricoModal} onOpenChange={setShowHistoricoModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Histórico de Movimentações de Cargo</DialogTitle>
              <DialogDescription>
                {historicoData?.employee?.nome} - {historicoData?.employee?.cargo_atual}
              </DialogDescription>
            </DialogHeader>

            {historicoData && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Resumo */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">{historicoData.resumo?.total_movimentacoes || 0}</p>
                    <p className="text-xs text-slate-500">Movimentações</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{historicoData.resumo?.efetivacoes || 0}</p>
                    <p className="text-xs text-slate-500">Efetivações</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{historicoData.resumo?.retornos || 0}</p>
                    <p className="text-xs text-slate-500">Retornos</p>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Histórico</h4>
                  {historicoData.historico?.length > 0 ? (
                    <div className="space-y-3">
                      {historicoData.historico.map((h, idx) => (
                        <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className={`p-2 rounded-full ${
                            h.tipo === 'efetivacao_cargo' ? 'bg-green-100' : 
                            h.tipo === 'retorno_cargo' ? 'bg-amber-100' : 'bg-slate-200'
                          }`}>
                            {h.tipo === 'efetivacao_cargo' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : h.tipo === 'retorno_cargo' ? (
                              <ArrowRight className="w-4 h-4 text-amber-600 rotate-180" />
                            ) : (
                              <History className="w-4 h-4 text-slate-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-slate-900">
                                  {h.tipo === 'efetivacao_cargo' ? 'Efetivação' : 
                                   h.tipo === 'retorno_cargo' ? 'Retorno ao Cargo' : 
                                   h.tipo === 'admissao' ? 'Admissão' : 'Movimentação'}
                                </p>
                                {h.cargo_de && h.cargo_para && (
                                  <p className="text-sm text-slate-600">
                                    {h.cargo_de} → {h.cargo_para}
                                  </p>
                                )}
                              </div>
                              <span className="text-xs text-slate-500">
                                {h.data ? new Date(h.data).toLocaleDateString('pt-BR') : '-'}
                              </span>
                            </div>
                            {h.motivo && (
                              <p className="text-sm text-slate-500 mt-1">{h.motivo}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Nenhuma movimentação registrada</p>
                  )}
                </div>

                {/* Atribuições */}
                {historicoData.atribuicoes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Atribuições de Atividades</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Cargo</TableHead>
                          <TableHead>Período</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historicoData.atribuicoes.map((a, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{TIPO_LABELS[a.tipo] || 'Função Agregada'}</TableCell>
                            <TableCell>{a.cargo_novo_titulo}</TableCell>
                            <TableCell className="text-sm">
                              {a.data_inicio ? new Date(a.data_inicio).toLocaleDateString('pt-BR') : '-'}
                              {a.data_fim_efetiva && ` - ${new Date(a.data_fim_efetiva).toLocaleDateString('pt-BR')}`}
                            </TableCell>
                            <TableCell>
                              <Badge className={STATUS_LABELS[a.status]?.color || 'bg-slate-100'}>
                                {STATUS_LABELS[a.status]?.label || a.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistoricoModal(false)}>
                Fechar
              </Button>
              <Button 
                onClick={() => generateHistoricoPDF(historicoData?.employee?.id)}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
